// TxnScreenRuntime — the injected I/O contract for the shared end-user
// transaction screen (<TxnScreen>). The screen carries NO HTTP client of its
// own: each host app (the platform designer / Run view today, the standalone
// runtime client in a later phase) fills this contract with its own
// transport, already bound to one project. Signatures mirror the platform's
// livepreview api functions minus the leading `project` argument so the
// designer adapter is a set of one-line bindings.

import type { ReactNode } from 'react'
import type { CustomizeTransport } from '../../customize/model'
import type { TxnBuildRuntime } from '../../customize/build/types'
import type { UdaTransport } from '../../uda/types'
import type { TxnRuntime } from '../context'
import type { TransactionModel } from '../types'
import type {
  ApprovalAnalytics,
  ApprovalBulkResult,
  ApprovalChange,
  ApprovalChildChange,
  ApprovalDelegation,
  ApprovalInstance,
  ApprovalListResponse,
  ApprovalOverride,
  ApprovalResponse,
  ApprovalSla,
  ApprovalSlaScanResult,
  ListResponse,
  LoadResponse,
} from '../contracts'
import type { RecordHistoryFetcher } from '../list/RecordHistoryModal'
import type { AliasFetcher, AliasWriter } from '../list/RecordAliasModal'
import type { AuditLogFetcher } from '../list/PreviewAuditModal'
import type { GridLayoutPersistence } from '../list/useGridLayout'
import type { ScreenPrefsTransport } from './screenPrefs'

// ── List query ───────────────────────────────────────────────────────────

// One resolved filter clause for a list request. Mirrors the platform's
// `ListFilter` (livepreview api) shape exactly — `value`/`from`/`to` are
// already concrete primitives.
export interface ListFilter {
  column: string
  op:
    | 'eq' | 'neq'
    | 'gt' | 'gte' | 'lt' | 'lte'
    | 'between'
    | 'contains' | 'starts_with'
  value?: unknown
  from?: unknown
  to?: unknown
}

// End-user-adjusted values for the designer-enforced conditions, keyed by
// column db_name. They re-range/change a condition (unless the designer
// locked it) but cannot remove it.
export type EnforcedOverrides = Record<string, { from?: string; to?: string; value?: string }>

/** Everything one saved-records page fetch can carry: pagination, a child
 *  form's scope (formNo + parentPk), ad-hoc filters, and the enforced-filter
 *  selection (per-condition overrides + active set / bypass). */
export interface ListQuery {
  limit: number
  offset: number
  /** True when `limit` is a size the END USER picked (the list footer's
   *  Rows per page), rather than this client's own default. The engine
   *  otherwise substitutes the designer-configured page size for the main
   *  list, which made the selector inert: it asked for 100 and got 20. */
  explicitLimit?: boolean
  formNo?: string
  parentPk?: Record<string, unknown>
  filters?: ListFilter[]
  enforcedOverrides?: EnforcedOverrides
  filterSetId?: string
  bypass?: boolean
  /** The list TAB (`list_view.tabs`) by id, or 'all'. The server reads the
   *  tab's conditions from the model — the client only names it. */
  tab?: string
  /** Count every tab (first load / scope change / after a save — never on
   *  paging). */
  withTabCounts?: boolean
}

// ── Atomic multi-form save ───────────────────────────────────────────────
// Wire twins of the platform's BatchSaveBody / BatchSaveResult / BatchScope
// (livepreview api). Defined here so the shared screen types its save flow
// without importing the platform module.

export interface BatchSaveBody {
  header: Record<string, unknown>
  details: Record<string, Array<Record<string, unknown>>>
  txn_id?: string
  apply_item_change?: boolean
  // Warning codes the user acknowledged via "ignore warnings and proceed".
  ignore_warnings?: string[]
  // Reason for change, when the object demands one (REASON_REQUIRED retry).
  reason?: string
  reason_code?: string
  // Compare-and-set on the header: column → value the STORED record must
  // still hold, or the engine refuses with RECORD_CHANGED. An open editor in
  // the Run shell sends the values its edit started from.
  expect?: Record<string, unknown>
  // Replay protection: a host sends it as the `X-Idempotency-Key` header, so a
  // double tap or a retried request returns the first save instead of writing
  // twice (the Transaction Wizard's save step sets it). Never part of the body
  // the engine reads.
  idempotency_key?: string
}

export interface BatchSaveResult {
  ok: boolean
  txn_id: string
  header: Record<string, unknown>
  details: Record<string, Array<Record<string, unknown>>>
  cross_update_issues?: unknown[]
  // Informational severity-'P' prompts (validations / hooks) — the save
  // succeeded; show these to the user.
  prompts?: Array<{ code?: string | null; field?: string | null; message: string }>
}

/**
 * The screen as a FORM THAT RUNS SOMETHING instead of a record editor — an
 * IOFlow flow's input form (nav obj_type "J", docs/flow_forms.md).
 *
 * The screen opens straight into a new record and stays there: no list, no
 * Run-shell editor sessions, no "saved" state. Its one button posts the
 * buffer through `runtime.saveBatch` exactly as Save would — so item_change,
 * warnings confirms and row-scoped validation errors all behave as on any
 * screen — and hands the host's answer to `onDone` instead of promoting the
 * record to edit mode. The host's saveBatch decides what "submit" does.
 */
export interface TxnSubmitMode {
  /** The button's caption ("Run flow"). */
  label: string
  /** The form's title (defaults to the header form's title). */
  title?: string
  /** The host's answer for a submit that passed. The form keeps its values,
   *  so a person can change one thing and submit again. */
  onDone: (result: BatchSaveResult) => void
  /** Drawn under the form's title bar (a note from the host). */
  notice?: ReactNode
}

// Identifies the form/row that failed in a batch save so the client can route
// the error. `path` runs from the header (empty ⇒ header-level) down to the
// failing nested row. Carried in the error envelope's `details.scope`.
export interface BatchScope {
  form_no?: string | null
  path?: Array<{ form_no: string; index: number }>
}

// ── Approvals ────────────────────────────────────────────────────────────
// The full approval action family, project-bound. The screen itself only
// needs `list` (the toolbar badge count); the rest is declared so the
// standalone runtime's approvals inbox can adopt the same contract without
// churn — hosts that don't surface those flows may omit them.

export interface ApprovalsRuntime {
  list(
    opts?: { status?: string; objName?: string; all?: boolean; limit?: number; offset?: number },
  ): Promise<ApprovalListResponse>
  submissions?(
    opts?: { status?: string; objName?: string },
  ): Promise<{ ok: boolean; items: ApprovalInstance[]; count: number }>
  get?(instanceId: string, objName?: string): Promise<{
    ok: boolean
    instance: ApprovalInstance
    changes?: ApprovalChange[]
    child_changes?: ApprovalChildChange[]
    sla?: ApprovalSla
  }>
  approve?(
    instanceId: string,
    opts?: { objName?: string; activityId?: string; comment?: string; ignoreWarnings?: string[] },
  ): Promise<ApprovalResponse>
  reject?(
    instanceId: string,
    opts?: { objName?: string; activityId?: string; comment?: string },
  ): Promise<ApprovalResponse>
  edit?(
    instanceId: string,
    header: Record<string, unknown>,
    opts?: { objName?: string; details?: Record<string, unknown>; ignoreWarnings?: string[] },
  ): Promise<ApprovalResponse>
  withdraw?(instanceId: string, objName?: string): Promise<ApprovalResponse>
  bulk?(
    instanceIds: string[],
    opts?: { decision?: 'approve' | 'reject'; comment?: string; objName?: string },
  ): Promise<ApprovalBulkResult>
  analytics?(objName?: string): Promise<{ ok: boolean; analytics: ApprovalAnalytics }>
  delegates?: {
    list(opts?: { all?: boolean }): Promise<{ ok: boolean; items: ApprovalDelegation[] }>
    set(
      toUser: string,
      opts?: { fromUser?: string; startsAt?: string; endsAt?: string; reason?: string },
    ): Promise<{ ok: boolean; from_user: string; to_user: string }>
    revoke(delegationId: number): Promise<{ ok: boolean }>
  }
  overrides?: {
    list(): Promise<{ ok: boolean; overrides: ApprovalOverride[] }>
    set(
      name: string, value: string, opts?: { label?: string },
    ): Promise<{ ok: boolean; name: string; value: string }>
    remove(name: string): Promise<{ ok: boolean }>
  }
  slaScan?(objName?: string): Promise<ApprovalSlaScanResult>
}

// ── The screen contract ──────────────────────────────────────────────────

/**
 * The viewer's rights on one object, as the `/rights` endpoints report them.
 *
 * `actions` is the base vocabulary (`["*"]` = everything). `buttons` covers
 * the transaction's own action buttons: `restricted` names every button some
 * role has been granted BY NAME on this object, `granted` the ones the viewer
 * holds (`["*"]` = all). A button in neither list follows the base add/edit
 * right — see `canUseButton`. Older servers omit `buttons`; that reads as
 * "nothing restricted".
 */
export interface ObjectRightsSummary {
  can_read: boolean
  actions: string[]
  buttons?: { restricted: string[]; granted: string[] }
}

/** Whether the viewer may use the custom button `name`, given the base right
 *  is already held. One rule for every surface, mirroring the server's
 *  `authz.authorize_button`. */
export function canUseButton(rights: ObjectRightsSummary | null | undefined, name: string): boolean {
  if (!rights || !rights.buttons) return true
  const { restricted, granted } = rights.buttons
  if (granted.includes('*') || granted.includes(name)) return true
  return !restricted.includes(name)
}

export interface TxnScreenRuntime {
  /** The shared form-stack transport (lookup / save master / upload /
   *  run_action / notifications). The screen wraps its tree in
   *  <TxnRuntimeProvider value={runtime.txn}>. */
  txn: TxnRuntime
  /** Fetch one page of saved records (header or a child form's rows). */
  list(objName: string, q: ListQuery): Promise<ListResponse>
  /** Load one record's header for the editor. */
  load(objName: string, txnId: string): Promise<LoadResponse>
  /** Atomic multi-form save (header + buffered child tree). */
  saveBatch(objName: string, body: BatchSaveBody): Promise<BatchSaveResult>
  /** Delete one record. */
  remove(objName: string, txnId: string): Promise<unknown>
  /** The generic execute funnel — item_change / validate / run_action /
   *  run_action_bulk / list ids_only / the approval_* family all ride it. */
  execute(objName: string, action: string, payload?: Record<string, unknown>): Promise<unknown>
  /** Resolve project-variable defaults for the Add form. ``protect`` is the
   *  per-column lock state a Vision bean's ``itm_default`` returned
   *  ({form_key: 1 lock | 0 unlock}) — J2EE runtime only, absent elsewhere. */
  resolveDefaults(objName: string): Promise<{ ok: boolean; values: Record<string, unknown>; protect?: Record<string, 0 | 1> }>
  /** Per-record change-history fetcher (RecordHistoryModal transport). */
  recordHistory: RecordHistoryFetcher
  /** Per-record alias read/write (RecordAliasModal transport). Optional: a
   *  host that omits them simply never offers the "Also known as" action, so
   *  a surface without the endpoints degrades to today's behaviour. */
  recordAliases?: AliasFetcher
  saveRecordAliases?: AliasWriter
  /** Project-wide audit-log fetcher. Optional — admin-only surfaces hide
   *  when absent (the designer opens its audit modal from its own menu). */
  auditLog?: AuditLogFetcher
  /** Per-object end-user rights. Optional; the platform omits it (the
   *  designer preview always has full rights). Reserved for the standalone
   *  runtime adoption phase. */
  rights?(objName: string): Promise<ObjectRightsSummary>
  /** Reload the transaction model. Optional; hosts that own the model pass
   *  it as a prop instead. */
  getModel?(objName: string): Promise<TransactionModel>
  /** Approval action family (badge count + inbox flows). */
  approvals: ApprovalsRuntime
  /** Where "Apply layout" persists column width/order. Optional — when
   *  absent the layout apply affordance fails with a notify.error. */
  layout?: Partial<GridLayoutPersistence>
  /** Customize mode (a tenant's functional administrator re-presenting this
   *  screen for their organization). Absent ⇒ the mode is not offered. */
  customize?: {
    transport: CustomizeTransport
    /** Whether the caller may customize (resolved once per screen). */
    canCustomize: () => Promise<boolean>
  }
  /** Make changes — the same mode pointed at the transaction's CORE model,
   *  for people who build the app (platform only). Absent ⇒ not offered. */
  build?: TxnBuildRuntime
  /** Additional attributes (user-defined attributes): the setup transport of
   *  this host. Present ⇒ the screen asks `uda_resolve` (through `execute`)
   *  which attributes apply to the record / lines / list, for forms the
   *  engine marks as having them (`_uda`). Absent ⇒ no attributes, no extra
   *  request, and the screen renders exactly as it did before. */
  uda?: UdaTransport
  /** Per-user, per-screen preferences (list layout, editor layout, last
   *  tab) on the server — the user-memory `__screens__` record. Absent ⇒ the
   *  screen remembers them in this browser only. */
  screenPrefs?: ScreenPrefsTransport
  /** Prepare the backing store before the first fetch (the designer deploys
   *  the preview schema here; the standalone runtime omits it). Rejecting
   *  keeps the screen un-ready — the host is expected to have surfaced the
   *  failure itself. Re-invoked when the host bumps `versionKey`. */
  ensureReady?(objName: string): Promise<void>
}

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Validate ONE form's payload on demand, without writing — the same checks a
 * save runs for that form (mandatory fields + field-level + form-level
 * rules). Resolves on success; throws the standard 422 envelope on failure.
 * Twin of the platform's `livePreviewValidate`, built on the execute funnel.
 *
 * `txnId` — the record this payload belongs to — is REQUIRED, deliberately: it
 * is what tells the engine to validate an edit rather than an insert, and a
 * caller that omits it silently gets add-mode checks against a saved record.
 * Record-aware rules then see the record's own stored row as a foreign
 * duplicate and `must_be_unique` flags the value the user never touched. Pass
 * the id whenever a record is open; pass `undefined` ONLY for a genuinely new
 * unsaved one. Making it required (not optional) is what stops a future call
 * site from reintroducing that bug by forgetting it.
 */
export async function executeValidate(
  runtime: Pick<TxnScreenRuntime, 'execute'>,
  objName: string,
  formNo: string | undefined,
  header: Record<string, unknown>,
  opts: {
    headerValues?: Record<string, unknown>
    parentPk?: Record<string, unknown>
    txnId: string | undefined
    // Warning codes the user already confirmed — the retry after the
    // warnings OK / Cancel prompt.
    ignoreWarnings?: string[]
  },
): Promise<void> {
  const { headerValues, parentPk, txnId, ignoreWarnings } = opts
  await runtime.execute(objName, 'validate', {
    ...(formNo ? { form_no: formNo } : {}),
    header,
    header_values: headerValues || {},
    form_rules_only: false,
    ...(parentPk && Object.keys(parentPk).length > 0 ? { parent_pk: parentPk } : {}),
    ...(txnId ? { txn_id: txnId } : {}),
    ...(ignoreWarnings?.length ? { ignore_warnings: ignoreWarnings } : {}),
  })
}
